/* c-scroller.js — horizontal scroller with the design's progress thumb.

   Markup contract:
     <c-scroller>
       <div data-scroller-viewport>…cards…</div>
       <div data-scroller-track><div data-scroller-thumb></div></div>
     </c-scroller>

   Native overflow scrolling stays in charge (touch, trackpad, keyboard). On top
   of it this adds what a mouse has no gesture for: drag the cards to pan, and
   drag the thumb. Scroll snapping is suspended for the duration of a drag —
   with `scroll-snap-type: mandatory` every small programmatic move is otherwise
   snapped straight back to where it started. */
(function () {
  'use strict';

  function CScroller() {
    return Reflect.construct(HTMLElement, [], CScroller);
  }

  CScroller.prototype = Object.create(HTMLElement.prototype);
  CScroller.prototype.constructor = CScroller;
  Object.setPrototypeOf(CScroller, HTMLElement);

  CScroller.prototype.connectedCallback = function () {
    this.viewport = this.querySelector('[data-scroller-viewport]');
    this.track = this.querySelector('[data-scroller-track]');
    this.thumb = this.querySelector('[data-scroller-thumb]');
    if (!this.viewport) return;

    var self = this;
    this.update = this.update.bind(this);

    this.viewport.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.viewport);
    }

    // Bind to the track, not the fill: the fill is 0px wide at the start of the
    // run, so there would be nothing to grab.
    if (this.track) this.bindDrag(this.track, 'thumb');
    this.bindDrag(this.viewport, 'content');

    // Belt and braces: any element that slips past the CSS must not start a
    // native drag either.
    this.viewport.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    // A drag that travelled must not also fire the link/click underneath it.
    this.viewport.addEventListener(
      'click',
      function (event) {
        if (self.travelled > 4) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    this.update();
  };

  CScroller.prototype.bindDrag = function (element, mode) {
    var self = this;

    element.addEventListener('pointerdown', function (event) {
      // Touch and pen already pan natively; only take over for the mouse.
      if (mode === 'content' && event.pointerType !== 'mouse') return;
      if (event.button !== undefined && event.button !== 0) return;
      if (!self.canScroll()) return;

      self.dragMode = mode;
      self.pointerId = event.pointerId;
      self.startX = event.clientX;
      self.startScroll = self.viewport.scrollLeft;
      self.travelled = 0;
      self.captured = false;

      // Capturing here would retarget the whole pointer sequence — click
      // included — at the viewport, so a button or link inside a card could
      // never be clicked with a mouse. Content drags capture only once the
      // pointer has actually travelled; the thumb has nothing to click.
      if (mode === 'thumb') {
        self.setAttribute('data-dragging', mode);
        self.seekTo(event.clientX);
        self.capture(element, event.pointerId);
        event.preventDefault();
      }
    });

    element.addEventListener('pointermove', function (event) {
      if (self.dragMode !== mode) return;

      var travel = event.clientX - self.startX;
      self.travelled = Math.abs(travel);

      if (mode === 'content') {
        if (self.travelled <= 4) return;
        if (!self.captured) {
          self.setAttribute('data-dragging', mode);
          self.capture(element, self.pointerId);
        }
      }

      if (mode === 'thumb') {
        self.seekTo(event.clientX);
      } else {
        self.viewport.scrollLeft = self.startScroll - travel;
      }
      event.preventDefault();
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (type) {
      element.addEventListener(type, function () {
        if (self.dragMode !== mode) return;
        self.dragMode = null;
        self.captured = false;
        self.removeAttribute('data-dragging');
        // Let the click guard see the distance, then reset it.
        window.setTimeout(function () {
          self.travelled = 0;
        }, 0);
      });
    });
  };

  CScroller.prototype.capture = function (element, pointerId) {
    try {
      element.setPointerCapture(pointerId);
      this.captured = true;
    } catch (e) {
      /* synthetic events have no active pointer to capture; drag still works */
    }
  };

  // Map a pointer position on the track to a scroll offset.
  CScroller.prototype.seekTo = function (clientX) {
    var track = this.track || this.viewport;
    var rect = track.getBoundingClientRect();
    if (!rect.width) return;
    var ratio = (clientX - rect.left) / rect.width;
    ratio = Math.min(Math.max(ratio, 0), 1);
    this.viewport.scrollLeft = ratio * (this.viewport.scrollWidth - this.viewport.clientWidth);
  };

  CScroller.prototype.canScroll = function () {
    return this.viewport.scrollWidth > this.viewport.clientWidth + 1;
  };

  CScroller.prototype.disconnectedCallback = function () {
    window.removeEventListener('resize', this.update);
    if (this.observer) this.observer.disconnect();
  };

  // The bar is a progress fill, not a scrollbar thumb: it is anchored at the
  // left of the track and grows as the viewport is scrolled, reaching the full
  // width at the end of the run.
  CScroller.prototype.update = function () {
    var scrollable = this.canScroll();
    this.toggleAttribute('data-scroller-idle', !scrollable);
    if (!scrollable || !this.thumb) return;

    var maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    var progress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 0;
    progress = Math.min(Math.max(progress, 0), 1);

    this.thumb.style.width = (progress * 100).toFixed(3) + '%';
  };

  if (window.customElements && !window.customElements.get('c-scroller')) {
    window.customElements.define('c-scroller', CScroller);
  }
})();
